import React from "react"
import { Link } from "gatsby"
import styled from "styled-components"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import Tabs from "components/common/tabs"
import ContentWrapper from "styles/contentWrapper"

const lampImg = 'https://hrflow-ai.imgix.net/lamp.svg'


const StyledSection = styled.section`
  width: 100%;
  height: auto;
  padding: 3rem 0;
`

const StyledContentWrapper = styled(ContentWrapper)`
  && {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding-right: 0;
    padding-left: 0;
    max-width: unset;
    .nopadding {
      padding: 0!important;
    }
    @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
      padding-right: 0;
      padding-left: 2.5rem;
    }
    .section-title {
      color: ${({ theme }) => theme.colors.primary};
      text-align: center;
      margin-top: 0;
      margin-bottom: 2rem;
      padding: 0 1rem;
      @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
        padding-right: 0;
        padding-left: 0;
      }
    }

    .tab-list {
      @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        display: grid;
        -webkit-mask-image: linear-gradient(to right, black 80%, transparent 100%);
        /* Calculate how many columns are needed, depending on clients count */
        grid-template-columns: repeat(8, 10rem);
        grid-template-rows: repeat(1, auto);
        grid-auto-flow: column;
        column-gap: 1rem;
        row-gap: 1rem;
        padding: 0 2.5rem 0 2.5rem;
        overflow-x: scroll;
        overflow-y: hidden;
        margin-top: 2rem;
        -webkit-overflow-scrolling: touch;
        &::-webkit-scrollbar {
          display: none;
        }
        /* Workaround: https://stackoverflow.com/questions/38993170/last-margin-padding-collapsing-in-flexbox-grid-layout */
        &::after {
          content: "";
          width: ${({ itemCount }) =>
            Math.ceil(itemCount / 2) % 2 === 1 ? "17.125rem" : "2.5rem"};
        }
        @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
          grid-auto-flow: row;
          grid-template-columns: repeat(8, 10rem);
          overflow: visible;
          padding: 0;
        }
        /* Show scrollbar if desktop and wrapper width > viewport width */
        @media (hover: hover) {
          scrollbar-color: ${({ theme }) => theme.colors.scrollBar} transparent; // Firefox only
          &::-webkit-scrollbar {
            display: block;
            -webkit-appearance: none;
          }
      
          &::-webkit-scrollbar:horizontal {
            height: 0.8rem;
          }
      
          &::-webkit-scrollbar-thumb {
            border-radius: 8px;
            border: 0.2rem solid ${({ theme }) => theme.colors.background};
            background-color: ${({ theme }) => theme.colors.scrollBar};
          }
      
          &::-webkit-scrollbar-track {
            background-color: ${({ theme }) => theme.colors.background};
            border-radius: 8px;
          }
        }

        .tab-list-item {
          width: 10rem!important;
          height: 3rem!important;
          font-size: 0.8rem!important;
          margin: 0.5 1rem 0 0!important;
        }
      }
      @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
        max-width: 90rem;
        margin: auto;
        margin-top: 3rem;
        justify-content: center;
      }
     
      .tab-list-item {
        background: #fff;
        border: .1rem solid #bddae3;
        box-sizing: border-box;
        border-radius: 10rem;
        height: 4rem;
        width: 14rem;
        margin: 1rem 1.5rem 0 0;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1rem;
        text-align: center;
        letter-spacing: -.05rem;
        color: ${({ theme }) => theme.colors.primary};
        font-weight: 600;
        &::before {
          display: none;
        }
        &:hover, &.tab-list-active {
          background: ${({ theme }) => theme.colors.primary}!important;
          border: .1rem solid ${({ theme }) => theme.colors.primary}!important;
          color: #fff!important;
          -webkit-transition: all .3s ease-in;
          transition: all .3s ease-in;
          text-decoration: none;
        }
      }
    }
  }
`

const StyledInnerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: -3rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: row;
    margin-left: 0;
    justify-content: space-between;
  }
  @media (min-width: 1400px) {
    margin-left: calc((100% - 90rem)/2 + 2.5rem);
  }
`

const StyledDetails = styled.div`
  padding: 0 1rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 30%;
    flex: 1 1 30%;
    padding: 0;
  }
  h3 {
    margin-bottom: 2rem;
  }
  .text {
    margin-bottom: 2rem;
  }

  .text-bold {
    font-size: 1.2rem;
    margin-bottom: 2rem;
  }

  .highlights-list {
    margin: 1.5rem 0;
    padding: 0;
    list-style: none;
    li {
      display: flex;
      max-width: 30rem;
      min-height: 5.55rem;
      padding-top: 1rem;
      &:nth-child(odd) {
        background: radial-gradient(279.5px at 50% 49.55%,
          #f3f8f9 0,
          rgba(243, 248, 249, .76) 55.8%,
          rgba(243, 248, 249, 0) 100%
        );
      }
      .tag {
        min-width: 4rem;
        height: 1.8rem;
        padding: padding: 1rem 1.3rem 1rem 1rem;
        text-align: center;
        background: ${({ theme }) => theme.colors.secondary};
        color: ${({ theme }) => theme.colors.white};
        margin-right: 1rem;
        border-radius: 3px;
        font-weight: bold;
      }
      span {
        color: ${({ theme }) => theme.colors.secondary};
        font-weight: bold;
        margin-right: 0.5rem;
      }
    }
  }

`

const StyledMedia = styled.div`
  position: relative;
  margin-top: -4rem;
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 70%;
    flex: 1 1 70%;
    margin-top: 0;
  }
  .mackbook {
    display: block;
  }
  .image {
    position: absolute;
    top: 4rem;
    right: 0;
    z-index: 1;
    width 78%;
    height: auto;
    @media (min-width: 350px) {
      top: 4.5rem;
    }
    @media (min-width: 400px) {
      top: 5.3rem;
    }
    @media (min-width: 500px) {
      top: 7rem;
    }
    @media (min-width: 600px) {
      top: 8rem;
    }
    @media (min-width: 700px) {
      top: 10rem;
    }
    @media (min-width: 900px) {
      top: 11rem;
    }
    @media (min-width: 1000px) {
      top: 13rem;
    }
    @media (min-width: 1400px) {
      top: 12rem;
    }
    @media (min-width: 1600px) {
      top: 14rem;
    }
  }
`

const PowerdBy = styled.div`
  display: flex;
  align-items: center;
  .logo  {
    background-image: url(${lampImg});
    background-size: contain;
    background-repeat: no-repeat;
    height: 2.5rem;
    width: 2.5rem;
    margin-right: 0.5rem;
    @media (min-width: ${({ theme }) => theme.breakpoints.sm}) {
      background-size: 3.5rem 3.5rem;
      background-repeat: no-repeat;
      height: 3.5rem;
      width: 3.5rem;
      margin-right: 1rem;
    }
  }
`

const useCases = ({ content }) => {
  const { exports, frontmatter } = content[0].node
  const { cases } = exports
  const imageLaptop = getImage(frontmatter.imageLaptop)
  return (
    <StyledSection id="usecases">
      <StyledContentWrapper>
        <h2 className="section-title">{frontmatter.title}</h2>
        <div className="text text-center">{frontmatter.subtitle}</div>
        <Tabs minHeight="unset" maxHeight="unset"> 
          {cases.map(({title, description, sources, processing, destinations, image, poweredBy}, key) => {
            const pic = getImage(image)
            return (
              <div key={key} label={title}> 
                <StyledInnerWrapper>
                  <StyledDetails>
                    <h3>{title}</h3>
                    <div className="text">{description}</div>
                    <div className="text-bold">Data flow</div>
                    <ul className="highlights-list">
                      <li>
                        <span className="tag">1</span> <span>Sources: </span>{sources}
                      </li>
                      <li>
                        <span className="tag">2</span> <span>Processing: </span>{processing}
                      </li>
                      <li>
                        <span className="tag">3</span> <span>Destination: </span>{destinations}
                      </li>
                    </ul>
                    <PowerdBy>
                      <div className="logo"></div>
                      <span>
                        Powered by {poweredBy.map((p, index) => (
                          <span key={`${index}-p`}>
                            <Link to="/api/" >{p}</Link>{index  === 0 && poweredBy.length > 1  ? ' + ' : ''}
                          </span>
                        ))}
                      </span>
                    </PowerdBy>
                  </StyledDetails>
                  <StyledMedia>
                    <GatsbyImage className="image" alt={title} image={pic} />
                    <GatsbyImage className="mackbook" alt="Macbook air photo" image={imageLaptop} />
                  </StyledMedia>
                </StyledInnerWrapper>
              </div> 
            )
          })} 
        </Tabs>
      </StyledContentWrapper>
    </StyledSection>
  )
}

useCases.propTypes = {
}

export default useCases